import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import type { RootState } from "redux/reducers";
import herdService from "services/herdService";
import { getErrorMessage } from "shared/errorUtils";
import {
  exibirCarregando,
  listFarmCausesOfDeath,
  notifyError,
  notifySuccess,
  type CauseOfDeathDispatchAction,
} from "../actions";

export const fetchFarmCausesOfDeath =
  (withDeleted?: boolean) =>
  async (
    dispatch: Dispatch<CauseOfDeathDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      dispatch(exibirCarregando(true));
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const causesOfDeath = await herdService.fetchFarmCausesOfDeath(
        fazenda.FazendaSelecionada,
        withDeleted
      );

      dispatch(listFarmCausesOfDeath(causesOfDeath.data));
    } catch (error) {
      dispatch(listFarmCausesOfDeath([]));
      const message = getErrorMessage(
        error,
        t("livestock:fetch-cause-of-death-error")
      );
      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const deleteCauseOfDeath =
  (id: number) =>
  async (dispatch: Dispatch<CauseOfDeathDispatchAction>): Promise<void> => {
    try {
      await herdService.deleteCauseOfDeathById(id);

      dispatch(
        notifySuccess({
          mensagem: t("livestock:delete-cause-of-death-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:delete-cause-of-death-error")
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const activateCauseOfDeath =
  (
    id: number,
    payload: Parameters<typeof herdService.activateCauseOfDeath>[1]
  ) =>
  async (dispatch: Dispatch<CauseOfDeathDispatchAction>): Promise<void> => {
    try {
      await herdService.activateCauseOfDeath(id, payload);

      dispatch(
        notifySuccess({
          mensagem: t("livestock:activate-cause-of-death-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:activate-cause-of-death-error")
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const createCauseOfDeath =
  (
    payload: Omit<
      Parameters<typeof herdService.createCauseOfDeath>[0],
      "farmId"
    >
  ) =>
  async (
    dispatch: Dispatch<CauseOfDeathDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        dispatch(notifyError({ mensagem: t("common:no-farm-selected") }));
        return;
      }

      await herdService.createCauseOfDeath({
        ...payload,
        farmId: fazenda.FazendaSelecionada,
      });

      dispatch(
        notifySuccess({
          mensagem: t("livestock:create-cause-of-death-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:create-cause-of-death-error")
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateCauseOfDeath =
  (id: number, payload: Parameters<typeof herdService.updateCauseOfDeath>[1]) =>
  async (dispatch: Dispatch<CauseOfDeathDispatchAction>): Promise<void> => {
    try {
      await herdService.updateCauseOfDeath(id, payload);

      dispatch(
        notifySuccess({
          mensagem: t("livestock:update-cause-of-death-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:update-cause-of-death-error")
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };
